export { stateHasWasteCodes } from "./stateWasteCodes";

export const STATES_NOT_FOLLOWING_FEDERAL_GENERATOR_STATUS = [
  "CA",
  "KS",
  "ME",
  "MD",
  "MA",
  "MN",
  "RI",
  "TX",
  "WA",
  "DC",
];
const statesNotFollowingFederalGeneratorStatus = new Set(
  STATES_NOT_FOLLOWING_FEDERAL_GENERATOR_STATUS
);

export const stateDoesNotFollowFederalGeneratorStatus = (
  state: string | null | undefined
): boolean => {
  if (!state) return false;
  return statesNotFollowingFederalGeneratorStatus.has(state.toUpperCase());
};

/**
 * States that do not require OI (Off-site Installation) forms in RCRA flat-file exports.
 * These states do not require destination address or transporter address data for reporting.
 */
export const STATES_WITHOUT_OI_FORMS = ["KS", "MD", "MI", "NC", "OK", "PA", "WI"];
const statesWithoutOiForms = new Set(STATES_WITHOUT_OI_FORMS);

export const stateRequiresOiForms = (state: string | null | undefined): boolean => {
  if (!state) return true; // Default to requiring OI forms when state is unknown
  return !statesWithoutOiForms.has(state.toUpperCase());
};

/**
 * States that require generators to register each new waste stream with
 * the state. The state assigns an identifier (e.g. TN `WS-1`, `WS-2`)
 * which is stored on `StateWasteStreamRegistration.stateRegisteredId`.
 */
export const WASTE_STREAM_REGISTRATION_REQUIRED_STATES = ["TN", "SC", "KY", "MN", "TX"] as const;
export type WasteStreamRegistrationRequiredState =
  (typeof WASTE_STREAM_REGISTRATION_REQUIRED_STATES)[number];

const wasteStreamRegistrationRequiredStates = new Set<string>(
  WASTE_STREAM_REGISTRATION_REQUIRED_STATES
);

export const stateRequiresWasteStreamRegistration = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return wasteStreamRegistrationRequiredStates.has(state.toUpperCase());
};

// Some states' registries publish only the federal waste codes and a name —
// no form code, no DOT shipping info. For those, the registration matcher
// scores on federal codes + name alone (the form and DOT dimensions are
// dropped and their weight redistributed), so a stream that matches on the
// signals the state actually provides isn't capped by neutral scores on
// dimensions it can never populate. Kentucky is the first such state.
export const REGISTRATION_STATES_WITHOUT_FORM_OR_DOT: readonly string[] = ["KY"];

export const registrationStateOmitsFormAndDot = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return REGISTRATION_STATES_WITHOUT_FORM_OR_DOT.includes(state.toUpperCase());
};

/**
 * States where the registration's state-assigned id is itself a state waste
 * code that rides the stream's manifests — so a stream carrying that id among
 * its `stateWasteCodes` is that registration's stream. Texas is the first: its
 * 8-char TCEQ waste code both registers the stream and appears on its lines.
 * Contrast TN/KY, whose ids are opaque sequences (e.g. `WS-1`) that never
 * appear on a stream. Used by the registration matcher to score and to gate
 * linkability on a state-code match.
 */
export const REGISTRATION_ID_IS_STATE_WASTE_CODE_STATES = ["TX"] as const;
const registrationIdIsStateWasteCodeStates = new Set<string>(
  REGISTRATION_ID_IS_STATE_WASTE_CODE_STATES
);

export const registrationIdIsStateWasteCode = (state: string | null | undefined): boolean =>
  !!state && registrationIdIsStateWasteCodeStates.has(state.toUpperCase());
